import { FocusMonitor } from '@angular/cdk/a11y';
import { NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    NgZone,
    OnDestroy,
    Renderer2,
    ViewEncapsulation,
    WritableSignal,
    booleanAttribute,
    contentChildren,
    inject,
    input,
    model,
    output,
    signal,
    viewChild,
    viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { filter, take } from 'rxjs';

import { SEGMENTED_CONTROL_OPTIONS_TOKEN } from './segmented-control.token';
import { DsSegmentedOption } from './segmented-option.component';

@Component({
    selector: 'ds-segmented-control',
    templateUrl: './segmented-control.component.html',
    styleUrls: ['./segmented-control.component.scss'],
    host: {
        class: `ds-segmented-control`,
    },
    imports: [NgTemplateOutlet],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsSegmentedControl implements AfterViewInit, OnDestroy {
    private readonly controlOptions = inject(SEGMENTED_CONTROL_OPTIONS_TOKEN);
    private readonly renderer = inject(Renderer2);
    private readonly ngZone = inject(NgZone);
    private readonly focusMonitor = inject(FocusMonitor);
    private readonly destroyRef = inject(DestroyRef);
    public readonly activeOption = model('');
    public readonly optionChanged = output<{ name: string; event: Event }>();
    public readonly fullWidth = input(this.controlOptions.fullWidth, { transform: booleanAttribute });
    public readonly roleType = input<'radiogroup' | 'tablist'>('tablist');
    public readonly twoLineTruncation = input(false, { transform: booleanAttribute });
    public readonly inverse = model<boolean>(false);

    protected readonly scContainer = viewChild.required<ElementRef<HTMLDivElement>>('scContainer');
    protected readonly segmentedOptions = contentChildren(DsSegmentedOption);
    protected readonly tabLabels = viewChildren<ElementRef<HTMLDivElement>>('tabOption');

    protected readonly isReady = signal(false);
    public readonly selectedOption: WritableSignal<DsSegmentedOption | null> = signal<DsSegmentedOption | null>(null);
    public readonly focusedOption: WritableSignal<DsSegmentedOption | null> = signal<DsSegmentedOption | null>(null);
    public readonly focusVisibleOption: WritableSignal<DsSegmentedOption | null> = signal<DsSegmentedOption | null>(null);
    private readonly resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
            this._setIndicatorCSSVars(entry.target as HTMLDivElement);
        }
    });

    ngAfterViewInit(): void {
        this.tabLabels().forEach((option, index) => {
            this.focusMonitor.monitor(option.nativeElement, true).subscribe((focusOrigin) => {
                const isFocused = focusOrigin === 'keyboard' || focusOrigin === 'program';
                if (isFocused) {
                    this.focusedOption.set(this.segmentedOptions()[index] ?? null);
                    this.focusVisibleOption.set(this.segmentedOptions()[index] ?? null);
                }
            });
        });

        // Initialize once on first stable render
        this.ngZone.onStable.pipe(take(1)).subscribe(() => {
            this.isReady.set(true);
            this.initializeSelection();
        });

        // Automatically re-sync activeOption highlight whenever DOM stabilizes
        this.ngZone.onStable
            .pipe(
                filter(() => this.isReady() && this.activeOption() !== this.selectedOption()?.name()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                const activeName = this.activeOption();
                const option = this.segmentedOptions().find((x) => x.name() === activeName);
                if (option) {
                    this.selectedOption.set(option);
                    this._setHighlightWidthAndXPost(option);
                }
            });
    }

    //Initialize the selection without triggering user-based tracking
    private initializeSelection(): void {
        const options = this.segmentedOptions();
        const activeName = this.activeOption();

        const option = options.find((x) => x.name() === activeName) ?? options[0];

        if (!option) {
            throw new Error('Please provide segmented options!');
        }

        this.selectedOption.set(option);
        this.activeOption.set(option.name());

        if (this.isReady()) {
            this._setHighlightWidthAndXPost(option);
        }
    }

    /**
     * The method which will update the `selected` signal in `ds-segment-option` based on the selected name.
     * @param name Name of the selected option
     * @param event
     */
    selectOption(name: string, event?: Event): void {
        const isUserInitiated = !!event;

        if ((!isUserInitiated && this.activeOption() === name && this.selectedOption()?.name() === name) || name === undefined) {
            return; // do nothing if the same option is clicked again
        }

        const option = this.segmentedOptions().find((x) => x.name() === name);
        if (option) {
            this.selectedOption.set(option);
            this.activeOption.set(name);

            if (this.isReady()) {
                this._setHighlightWidthAndXPost(option);
            }

            // Emit event only when triggered by user interaction
            if (isUserInitiated) {
                this.optionChanged.emit({ name, event });
            }
        } else {
            // if no option can be found, we select the first one by default
            this.selectFirstOption();
        }
    }

    /**
     * Select first segment option. This is useful when the activeOption is not provided.
     * @private
     */
    private selectFirstOption() {
        const options = this.segmentedOptions();
        if (options.length === 0) return;

        const firstOption = options[0] ?? null;

        this.selectedOption.set(firstOption);
        this.activeOption.set(firstOption?.name() ?? '');
    }

    /**
     * Will get the active segment position in order to show the indicator on the background.
     * @private
     */
    private _setHighlightWidthAndXPost(option: DsSegmentedOption) {
        for (const item of this.tabLabels()) {
            this.resizeObserver.unobserve(item.nativeElement);
        }

        const element = this.tabLabels().find((item) => item.nativeElement.id === `ds-segment-item-${option.name()}`);
        if (element) {
            this._setIndicatorCSSVars(element.nativeElement);
            this.resizeObserver.observe(element.nativeElement);
        }
    }

    /**
     * Will set the active element indicator related css variables
     * @private
     */
    private _setIndicatorCSSVars(element: HTMLDivElement) {
        const { offsetWidth, offsetLeft } = element;
        // We update the DOM directly, so we don't have to go through Angular Change Detection
        this.renderer.setProperty(
            this.scContainer().nativeElement,
            'style',
            `--ds-sc-highlight-width: ${offsetWidth}px; --ds-sc-highlight-x-pos: ${offsetLeft}px`,
        );
    }

    onKeydown(event: KeyboardEvent) {
        const { key } = event;
        const options = this.segmentedOptions();
        const currentIndex = options.findIndex((option) => option.focused());
        let newIndex: number | undefined;

        if (key === 'ArrowRight') {
            newIndex = (currentIndex + 1) % options.length;
        } else if (key === 'ArrowLeft') {
            newIndex = (currentIndex - 1 + options.length) % options.length;
        } else if ((key === ' ' || key === 'Enter') && currentIndex !== -1 && options[currentIndex]) {
            this.selectOption(options[currentIndex].name(), event);
        }

        if (newIndex !== undefined) {
            event.preventDefault();
            const newOption = options[newIndex];
            if (newOption) {
                this.focusOption(newOption, newIndex);
            }
        }
    }

    private focusOption(option: DsSegmentedOption, index: number) {
        const focusOption = this.tabLabels()[index];
        if (focusOption) {
            this.focusedOption.set(option);
            this.focusVisibleOption.set(option);
            this.focusMonitor.focusVia(focusOption.nativeElement, 'keyboard');
        }
    }

    ngOnDestroy() {
        if (this.tabLabels()) {
            this.tabLabels().forEach((option) => this.focusMonitor.stopMonitoring(option));
        }

        this.resizeObserver.disconnect();
    }
}
